import { Button, CopyButton, Group, Stack, Text } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from 'react-map-gl';

export interface SamplePopupData {
  longitude: number;
  latitude: number;
  code?: string;
  sampleId?: string | number | null;
  pointId?: string | number | null;
  fieldId?: string | number | null;
  status?: string | null;
  fieldName?: string | null;
  carbonContent?: number | null;
  soilDensity?: number | null;
}

interface SamplePopupProps {
  data: SamplePopupData | null;
  onClose: () => void;
}

export const SamplePopup: FC<SamplePopupProps> = ({ data, onClose }) => {
  const { t } = useTranslation();
  if (!data) return null;

  return (
    <Popup
      anchor="bottom"
      closeOnClick={false}
      closeOnMove={false}
      latitude={data.latitude}
      longitude={data.longitude}
      offset={[0, -15]}
      onClose={onClose}
    >
      <Stack>
        {data.sampleId != null && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.properties.sample_id')}
              </Text>
              <Text size="sm">{data.sampleId}</Text>
            </div>
          </Group>
        )}

        {data.pointId != null && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.properties.point_id')}
              </Text>
              <Text size="sm">{data.pointId}</Text>
            </div>
          </Group>
        )}

        {data.fieldId !== undefined && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.properties.field_id')}
              </Text>
              <Text size="sm">{data.fieldId ?? '—'}</Text>
            </div>
          </Group>
        )}

        {data.code ? (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.code')}
              </Text>
              <CopyButton value={data.code}>
                {({ copied, copy }) => (
                  <Button
                    color={copied ? 'pf-green.7' : 'pf-green.9'}
                    fullWidth
                    onClick={copy}
                    rightSection={copied ? '✓' : <IconCopy size={12} />}
                    size="compact-xs"
                    variant="outline"
                  >
                    {data.code}
                  </Button>
                )}
              </CopyButton>
            </div>
          </Group>
        ) : null}

        {/* Status Field - For Order and Sampling Plan */}
        {data.status && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('table.columns.status')}
              </Text>
              <Text size="sm">
                {t(`sampling.sample-status.${data.status.toLowerCase()}`)}
              </Text>
            </div>
          </Group>
        )}

        {/* Field Name - For Order and Sampling Plan */}
        {data.fieldName && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.parcelle')}
              </Text>
              <Text size="sm">{data.fieldName}</Text>
            </div>
          </Group>
        )}

        {/* Carbon Content - For Results */}
        {data.carbonContent !== undefined && data.carbonContent !== null && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.teneur-en-carbone')}
              </Text>
              <Text size="sm">{data.carbonContent} %</Text>
            </div>
          </Group>
        )}

        {/* Soil Density - For Results */}
        {data.soilDensity !== undefined && data.soilDensity !== null && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.densite-du-sol')}
              </Text>
              <Text size="sm">{data.soilDensity} g/cm³</Text>
            </div>
          </Group>
        )}
      </Stack>
    </Popup>
  );
};
